import { type FormEvent, type JSX, useState } from 'react';
import { Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';

import { Turnstile, TURNSTILE_ENABLED } from './Turnstile.js';

export interface SignupFormProps {
  onSubmit: (input: {
    email: string;
    companyName: string;
    turnstileToken?: string | null;
  }) => void;
  busy: boolean;
  /** Email-first entry: prefill the email captured by the gate. */
  initialEmail?: string;
  /** Hide the email field (the gate already shows it). */
  hideEmail?: boolean;
  /** Optional card heading + blurb (e.g. "Start a free fleet"). When the
   *  form stands on its own page next to sign-in, it needs a title. */
  title?: string;
  subtitle?: string;
}

/**
 * Free-tier signup form. Two fields: email + companyName. Server slugs
 * the company name into a tenantId; the email becomes the customer
 * record's PK so a duplicate-email signup returns the existing tenant.
 *
 * No password — license id is the long-term credential for paid tiers;
 * free tier doesn't need to log in to anything other than the launcher
 * itself (which has its own admin gesture + PIN gate when configured).
 */
export function SignupForm({ onSubmit, busy, initialEmail, hideEmail, title, subtitle }: SignupFormProps): JSX.Element {
  const [email, setEmail] = useState(initialEmail ?? '');
  const [companyName, setCompanyName] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    onSubmit({ email: email.trim(), companyName: companyName.trim(), turnstileToken });
  }

  const canSubmit =
    email.trim().length > 0 &&
    companyName.trim().length > 0 &&
    !busy &&
    (!TURNSTILE_ENABLED || turnstileToken !== null);

  return (
    <Card variant="outlined">
      <CardContent>
        {title !== undefined && (
          <Typography variant="h6" fontWeight={700}>{title}</Typography>
        )}
        {subtitle !== undefined && (
          <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>{subtitle}</Typography>
        )}
        <Box component="form" onSubmit={handleSubmit} data-testid="signup-form">
          <Stack spacing={2}>
            {hideEmail !== true && (
              <TextField
                label="Work email"
                type="email"
                placeholder="admin@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                fullWidth
                inputProps={{ autoComplete: 'email', 'data-testid': 'signup-email' }}
              />
            )}
            <TextField
              label="Company / fleet name"
              placeholder="Acme Logistics"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
              autoFocus={hideEmail === true}
              fullWidth
              inputProps={{ autoComplete: 'organization', maxLength: 80, 'data-testid': 'signup-company' }}
            />
            <Turnstile onToken={setTurnstileToken} />
            <Button
              type="submit"
              variant="contained"
              disabled={!canSubmit}
              data-testid="signup-submit"
              sx={{ alignSelf: 'flex-start', py: 1 }}
            >
              {busy ? 'Creating your launcher…' : 'Start free'}
            </Button>
            <Typography variant="body2" color="text.secondary">
              We seed a starter launcher config for your fleet. Point your kiosk-capable MDM (SOTI
              MobiControl, Intune, Workspace ONE, Scalefusion, Hexnode, Esper) at the URL we give
              you — or sideload the standalone APK. Upgrade to Pro / Enterprise anytime to unlock
              theming, multi-profile, and the offline-first bundle download.
            </Typography>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}
